'use client'
import { noto_sans, ubuntu } from '@/globalConstants';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import BlogCard from './BlogCard';
import { Pagination, PaginationItem, Skeleton } from '@mui/material';
import { SearchBar } from './SearchBar';
import Image from 'next/image';
import { getResponsiveImageUrl } from '@/lib/globalUtils';
import { useAppContext } from '@/contexts/AppContext';
import { tagToPathSegment } from '@/lib/tagSlug';
import { articleLanguage, articlePath } from '@/lib/articleLanguage';
import { ArticleLanguage } from '@/globalTypes';
const BASE_CLASS = 'content-listing';

// /articles and /hi/articles share this listing; each shows only its own
// language. Topics stay as they are: tags are English in both.
const TEXT = {
  en: {
    home: '/articles',
    subtitle: 'The Dharmayana Blog',
    otherLanguage: { label: 'हिंदी में पढ़ें', href: '/hi/articles', lang: 'hi' },
    popular: 'Most Popular Articles',
    recent: 'Recent Articles',
    popularTopics: 'POPULAR TOPICS',
    featuredTopics: 'FEATURED TOPICS',
    results: (count: number, term: string) => `${count} results for "${term}"`,
    noResults: (term: string) => `Sorry, we did not find any results for “${term}” `,
    clearSearch: 'CLEAR SEARCH',
    searchPlaceholder: 'Search for Topics, Articles',
  },
  hi: {
    home: '/hi/articles',
    subtitle: 'धर्मायण के हिंदी लेख',
    otherLanguage: { label: 'Read in English', href: '/articles', lang: 'en' },
    popular: 'सबसे लोकप्रिय लेख',
    recent: 'नए लेख',
    popularTopics: 'लोकप्रिय विषय',
    featuredTopics: 'चुनिंदा विषय',
    results: (count: number, term: string) => `"${term}" के लिए ${count} परिणाम`,
    noResults: (term: string) => `“${term}” के लिए कोई परिणाम नहीं मिला`,
    clearSearch: 'खोज हटाएँ',
    searchPlaceholder: 'विषय या लेख खोजें',
  },
};

// Derive the topic/popular groupings from a blog list. Pure so it can seed the
// initial state (server render) and re-run on the client when the list changes.
const deriveTopics = (list: any[]) => {
  let featuredTopics: any[] = [];
  [...list].forEach((content: any) => {
    featuredTopics = featuredTopics
      .concat(content.tags)
      .filter((item, index, self) => self.indexOf(item) === index);
  });
  const popularBlogs = [...list]
    .sort((a: { viewCount: number }, b: { viewCount: number }) => b.viewCount - a.viewCount)
    .slice(0, 5);
  const popularTopics: any[] = [];
  popularBlogs.forEach((blog: { tags: any[] }) => {
    (blog.tags || []).forEach((tag: any) => {
      if (!popularTopics.includes(tag)) popularTopics.push(tag);
    });
  });
  return { featuredTopics, popularBlogs, popularTopics };
};

const ContentListing = ({ initialContentList = [], language = 'en', showOtherLanguage = false }: {
  initialContentList?: any[],
  language?: ArticleLanguage,
  // Link to the other language's listing; off while that one has no articles.
  showOtherLanguage?: boolean,
}) => {
  const text = TEXT[language];
  const ownLanguage = (blogs: any[]) => blogs.filter((blog: any) => articleLanguage(blog) === language);

  const pathname = usePathname();
  const { isAppView } = useAppContext();
  const { replace } = useRouter();
  const [contentList, setcontentList] = useState<any>(initialContentList);
  const router = useRouter();
  const searchParams: any = useSearchParams();
  // ?page=N is the recent-articles page, so each page is a real, crawlable URL
  // (the pagination buttons were JS-only, and older articles had no HTML links).
  const [paginationNumber, setPaginationNumber] = useState(() => Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1));
  const [featuredTopics, setFeaturedTopics] = useState<string[]>(() => deriveTopics(initialContentList).featuredTopics);
  const [popularTopics, setPopularTopics] = useState<string[]>(() => deriveTopics(initialContentList).popularTopics);
  const [popularBlogs, setPopularBlogs] = useState<string[]>(() => deriveTopics(initialContentList).popularBlogs);
  const [loader, setLoader] = useState(false);
  const isFirstRender = useRef(true);

  // getResponsiveImageUrl reads window.innerWidth, so it can only run on the client.
  // Until mounted, fall back to an SSR-safe thumbnail so the server HTML matches the
  // first client render, then upgrade to the responsive image.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const cardImageUrl = (article: any) =>
    isMounted
      ? getResponsiveImageUrl(article)
      : (article?.banner?.thumbnail?.url || article?.banner?.small?.url || '');

  const [recentSearches, setRecentSearches] = useState([]);

  const handleSearch = (term: string) => {
    const params = new URLSearchParams(searchParams);
    // params.set("page", "1");

    if (term) {
      params.set("search", term);
      let searches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
      if (!searches.includes(term)) {
        if (searches.length >= 5) {
          searches.shift();
        }
        searches.push(term);
        localStorage.setItem('recentSearches', JSON.stringify(searches));
        setRecentSearches(searches);
      }
    } else {
      params.delete("search");
    }

    replace(`${pathname}?${params.toString()}`);

  }

  useEffect(() => {
    if (searchParams.get('search') !== null) {
      (async () => {
        setLoader(true)
        let latestArticles = (await (await fetch(`${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}blogs/search?search=${searchParams.get('search')}&language=${language}`)).json());
        // Filtered here too: a CMS without the language filter ignores the param.
        setcontentList(ownLanguage(latestArticles));
        setPaginationNumber(1);
        setLoader(false)
      })()
    } else if (isFirstRender.current && initialContentList.length) {
      // The default (no-search) list was already fetched server-side and rendered
      // on first paint; skip the redundant client fetch to avoid a skeleton flash.
    } else {
      fetchBlogs();
    }
    isFirstRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    const derived = deriveTopics(contentList);
    setFeaturedTopics(derived.featuredTopics);
    setPopularBlogs(derived.popularBlogs);
    setPopularTopics(derived.popularTopics);
  }, [contentList])

  const fetchBlogs = async () => {
    setLoader(true)
    let latestArticles = (await fetch(`${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}blogs`));

    latestArticles.json().then(blogs => {
      setcontentList(ownLanguage(blogs));
      setLoader(false);
    });
    // setcontentList(latestArticles);

  }



  const otherLanguageLink = showOtherLanguage && (
    <Link href={text.otherLanguage.href} lang={text.otherLanguage.lang} className={`text-[#EE9322] hover:underline ${noto_sans.className}`}>
      {text.otherLanguage.label}
    </Link>
  );

  return (
    <>
      <div className={`${BASE_CLASS} flex place-content-center ${isAppView ? 'px-[1rem] py-6' : 'pt-4 pb-10'}`}>
        <div className={`${BASE_CLASS}__container grid  gap-[1rem] md:gap-[1.5rem] w-[95%] md:w-[90%]     2xl:w-[70%] `}>
          <div className={`${BASE_CLASS}__deity-list-container grid gap-[20px] md:gap-[24px] `}>
            <div className={`grid lg:flex gap-6 lg:gap-12 ${isAppView ? 'bg-white lg:bg-transparent rounded-2xl  border-[#8F8B8B] p-4' : ''}`} >
              {
                Boolean(searchParams.get('search')) ?
                  !contentList.length ?
                    <div className='flex flex-col gap-[12px] items-center w-full h-[356px] order-2 lg:order-1'>
                      <h2 className={`text-[#1D4749] ${ubuntu.className} font-[500]  text-[1.5rem]  w-full text-center lg:text-left`}>
                        {
                          text.results(contentList.length, searchParams.get('search'))
                        }
                      </h2>
                      <Image src="no-result.svg" alt="" width={100} height={100} ></Image>
                      <h2 className='text-center' >{text.noResults(searchParams.get('search'))}</h2>
                      <button onClick={() => {
                        if (isAppView)
                          router.push(`${text.home}?source=webview`);
                        else
                          router.push(text.home);

                      }} className={` w-[196px] h-[48px] py-[10px] px-[20px] rounded-[16px] border-1 border-[#E28C20] bg-[#EE9322] text-white ${ubuntu.className} font-[500] text-[16px] `}><p className='tracking-wide'>{text.clearSearch}</p></button>
                    </div>
                    : <div className='flex flex-col gap-[1rem] order-2 lg:order-1 '>
                      <h2 className={`text-[#1D4749] ${ubuntu.className} font-[500]  text-[1.5rem] `}>
                        {
                          text.results(contentList.length, searchParams.get('search'))
                        }
                      </h2>
                      {
                        [...contentList].map((article: any) => {
                          return (
                            <Link key={article.slug} href={isAppView ? `${articlePath(article)}?source=webview` : articlePath(article)} className='block    h-auto'>
                              <BlogCard thumbnailUrl={cardImageUrl(article)} tags={article.tags} heading={article.heading} summary={article.tagline} publishDate={article.publishDate} readTime={article.readTime} orientation={'flex'} slug={article.slug} />
                            </Link>
                          )
                        })
                      }
                    </div> : <div className={`order-2 lg:order-1 grid content-start gap-6`}>
                    {!isAppView && <div className={`hidden lg:flex gap-2 items-baseline`}>
                      <h1 className={`text-[#1D4749] text-[2rem] ${ubuntu.className} font-[500]`}>Sacred Threads</h1>
                      <p className={`text-[#8F8B8B] ${noto_sans.className}`}>{text.subtitle}</p>
                      {otherLanguageLink}
                    </div>}
                    <div className={`${BASE_CLASS}__container grid content-start gap-8 lg:basis-2/3`}>
                      {/*Popular Blogs*/}
                      <div className='flex flex-col gap-[1rem] '>
                        <h2 className={`text-[#1D4749] ${ubuntu.className} font-[500]  text-[1.5rem] `}>
                          {
                            loader ? <Skeleton variant='rounded' animation="wave"></Skeleton> : text.popular
                          }
                        </h2>
                        {
                          loader ? [1, 2, 3].map((n) => {
                            return (
                              <BlogCard key={`popular-skeleton-${n}`} thumbnailUrl={''} tags={[]} heading={''} summary={''} publishDate={''} readTime={0} orientation={'flex'} slug='' />
                            )
                          }) : [...popularBlogs].map((article: any) => {
                            return (
                              <Link key={article.slug} href={articlePath(article)} className='block    h-auto'>
                                <BlogCard thumbnailUrl={cardImageUrl(article)}
                                  tags={article.tags} heading={article.heading} summary={article.tagline} publishDate={article.publishDate} readTime={article.readTime} orientation={'flex'} slug={article.slug} />
                              </Link>
                            )
                          })
                        }
                      </div>
                      {/*Mobile popular topics */}
                      <div className={`grid gap-[1rem] ${ubuntu.className} font-[500]  lg:hidden `}>
                        <h2 className='text-[#1D4749]  text-[20px] p-[0.2rem] border-b-2 border-[#FFFFFF] '>{text.popularTopics}</h2>
                        <div className='flex flex-wrap gap-[0.8rem] text-[#EE9322] text-[16px]'>
                          {
                            popularTopics.map((topic: string, i: number) => {
                              return <Link key={`${topic}-${i}`} href={`/articles/tags/${tagToPathSegment(topic)}`}>
                                <span className='cursor-pointer hover:drop-shadow-sm' >
                                  {topic}
                                </span>
                              </Link>
                            })
                          }
                        </div>
                      </div>

                      <div className='flex flex-col gap-[1rem] '>
                        <h2 className={`text-[#1D4749] ${ubuntu.className} font-[500]  text-[1.5rem] `} id='recent'>
                          {
                            loader ? <Skeleton variant='rounded' animation="wave"></Skeleton> : text.recent
                          }
                        </h2>
                        {
                          loader ? [1, 2, 3].map((n) => {
                            return (
                              <BlogCard key={`recent-skeleton-${n}`} thumbnailUrl={''} tags={[]} heading={''} summary={''} publishDate={''} readTime={0} orientation={'flex'} slug='' />
                            )
                          }) : [...contentList].splice((paginationNumber - 1) * 5, 5).map((article: any) => {

                            return (
                              <Link key={article.slug} href={articlePath(article)} className='block    h-auto'>
                                <BlogCard thumbnailUrl={cardImageUrl(article)} tags={article.tags} heading={article.heading} summary={article.tagline} publishDate={article.publishDate} readTime={article.readTime} orientation={'flex'} slug={article.slug} />
                              </Link>
                            )
                          })
                        }
                      </div>
                      {/*Recent Articles*/}
                      <div className='flex justify-center'>
                        {contentList.length > 5 &&
                          <Pagination count={contentList.length < 5 ? 1 : Math.ceil(contentList.length / 5)} shape="rounded" page={paginationNumber}
                            renderItem={(item) => (
                              <PaginationItem
                                {...item}
                                component={Link}
                                href={item.page && item.page > 1 ? `${pathname}?page=${item.page}#recent` : `${pathname}#recent`}
                                scroll={false}
                                onClick={() => item.page && setPaginationNumber(item.page)}
                              />
                            )} />
                        }
                      </div>

                      {/*Mobile Featured Topics */}
                      <div className={`grid gap-[1rem] ${ubuntu.className} font-[500] font-[500] lg:hidden `}>
                        <h2 className='text-[#1D4749]  text-[20px] p-[0.2rem] border-b-2 border-[#FFFFFF]'>{text.featuredTopics}</h2>
                        <div className='flex flex-wrap gap-[0.8rem] text-[#EE9322] text-[16px]'>
                          {
                            featuredTopics.map((topic: string, i: number) => {
                              return <Link key={`${topic}-${i}`} href={`/articles/tags/${tagToPathSegment(topic)}`}>
                                <span className='cursor-pointer' >
                                  {topic}
                                </span>
                              </Link>
                            })
                          }
                        </div>
                      </div>
                    </div>
                  </div>
              }
              <div className={`order-1 lg:order-2 lg:basis-1/3 `}>
                <div className="grid gap-5 lg:gap-8">
                  <div className={`flex flex-wrap gap-2 items-baseline lg:hidden`}>
                    <h2 className={`text-[#1D4749] text-[2rem] ${ubuntu.className} font-[500]`}>Sacred Threads</h2>
                    <p className={`text-[#8F8B8B] ${noto_sans.className}`}>{text.subtitle}</p>
                    {otherLanguageLink}
                  </div>
                  <SearchBar handleSearch={handleSearch} recentSearches={recentSearches} placeholder={text.searchPlaceholder} />
                  <div className={` gap-[1rem] ${ubuntu.className} font-[500] hidden lg:grid`}>
                    <h2 className='text-[#1D4749]  text-[20px] p-[0.2rem] border-b-2 border-[#FFFFFF] '>{text.popularTopics}</h2>
                    <div className='flex flex-wrap gap-[0.8rem] text-[#EE9322] text-[16px]'>
                      {
                        popularTopics.map((topic: string, i: number) => {
                          return <Link key={`${topic}-${i}`} href={`/articles/tags/${tagToPathSegment(topic)}`}>
                            <span className='cursor-pointer hover:drop-shadow-sm' >
                              {topic}
                            </span>
                          </Link>
                        })
                      }
                    </div>
                  </div>

                  <div className={` gap-[1rem] ${ubuntu.className} font-[500] font-[500] hidden lg:grid `}>
                    <h2 className='text-[#1D4749]  text-[20px] p-[0.2rem] border-b-2 border-[#FFFFFF]'>{text.featuredTopics}</h2>
                    <div className='flex flex-wrap gap-[0.8rem] text-[#EE9322] text-[16px]'>
                      {
                        featuredTopics.map((topic: string, i: number) => {
                          return <Link key={`${topic}-${i}`} href={`/articles/tags/${tagToPathSegment(topic)}`}>
                            <span className='cursor-pointer' >
                              {topic}
                            </span>
                          </Link>
                        })
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>


    </>
  )
}

export default ContentListing


